import { useState, useEffect } from 'react'
import { Heading, Text, Box, Stack, Button, Icon, EmptyBox, SimpleTable, Input, FormControl, SimpleModal, Select, SearchableSelect, Badge, Alert } from '@/components/lib'
import { FaTrash } from 'react-icons/fa'

import api from '@/api'
import useStore from '@/store'
import utils from '@/utils/utils'

const RULE_TYPES = [
  { value: 'manual', label: 'Do not automatically trigger (manual only)' },
  { value: 'participant_login', label: 'When participant first logs in' },
  { value: 'stage_completion', label: 'When a stage is completed' }
]

function AnchorForm ({ anchor, onSave, onCancel, stages }) {
  const [name, setName] = useState(anchor?.name || '')
  const [description, setDescription] = useState(anchor?.description || '')
  const [ruleType, setRuleType] = useState(anchor?.assignmentRule?.type || 'manual')
  const [ruleStageId, setRuleStageId] = useState(anchor?.assignmentRule?.stageId || '')
  const [ruleStageRecurrence, setRuleStageRecurrence] = useState(
    anchor?.assignmentRule?.stageRecurrence != null ? String(anchor.assignmentRule.stageRecurrence) : ''
  )
  const [saving, setSaving] = useState(false)

  const selectedStage = stages?.find(s => s._id === ruleStageId)

  const stageOptions = (stages || []).map(s => ({ value: s._id, label: s.name }))

  const recurrenceOptions = (() => {
    if (!selectedStage) return []
    const opts = [{ value: '', label: 'Any recurrence' }]
    if (selectedStage.isRecurring && selectedStage.recurringDuration > 0) {
      for (let i = 0; i < selectedStage.recurringDuration; i++) {
        opts.push({ value: String(i), label: utils.recurrenceLabel(selectedStage, i) })
      }
    } else if (selectedStage.isAnchored && selectedStage.anchorSchedule?.length > 0) {
      selectedStage.anchorSchedule.forEach((slot, i) => {
        opts.push({ value: String(i), label: `Anchor slot ${i + 1} (day ${slot.dayOffset})` })
      })
    }
    return opts
  })()

  function buildAssignmentRule () {
    if (ruleType === 'manual') return { type: 'manual' }
    if (ruleType === 'participant_login') return { type: 'participant_login' }
    return {
      type: 'stage_completion',
      stageId: ruleStageId || null,
      stageRecurrence: ruleStageRecurrence !== '' ? parseInt(ruleStageRecurrence) : null
    }
  }

  function handleSave () {
    if (!name.trim()) {
      utils.toast('error', 'Anchor name is required')
      return
    }
    setSaving(true)
    onSave({ name: name.trim(), description: description.trim() || null, assignmentRule: buildAssignmentRule() }, () => setSaving(false))
  }

  return (
    <Stack gap={3}>
      <FormControl label='Name' isRequired>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder='e.g. Consent date' />
      </FormControl>
      <FormControl label='Description'>
        <Input value={description} onChange={e => setDescription(e.target.value)} placeholder='Optional description' />
      </FormControl>
      <FormControl label='Assignment rule' helperText='How is this anchor date determined for each participant?'>
        <Select value={ruleType} onChange={e => { setRuleType(e.target.value); setRuleStageId(''); setRuleStageRecurrence('') }}>
          {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </Select>
      </FormControl>
      {ruleType === 'stage_completion' && (
        <>
          <FormControl label='Stage'>
            <SearchableSelect
              options={stageOptions}
              value={ruleStageId}
              onChange={v => { setRuleStageId(v); setRuleStageRecurrence('') }}
              placeholder='Select a stage...'
            />
          </FormControl>
          {selectedStage && recurrenceOptions.length > 1 && (
            <FormControl label='Recurrence / slot'>
              <Select value={ruleStageRecurrence} onChange={e => setRuleStageRecurrence(e.target.value)}>
                {recurrenceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </FormControl>
          )}
        </>
      )}
      <Stack direction='row' justifyContent='end' gap={2}>
        <Button variant='outline' onClick={onCancel}>Cancel</Button>
        <Button colorPalette='primary' onClick={handleSave} loading={saving}>Save anchor</Button>
      </Stack>
    </Stack>
  )
}

function ruleDescription (rule, stages) {
  if (!rule || rule.type === 'manual') return 'Manual only'
  if (rule.type === 'participant_login') return 'On first participant login'
  if (rule.type === 'stage_completion') {
    const stage = stages?.find(s => s._id === rule.stageId)
    const stageName = stage?.name || rule.stageId || 'Unknown stage'
    // A name the admin typed is used as-is; the generic fallback stays lowercase
    // to read as part of the sentence.
    const recLabel = rule.stageRecurrence != null
      ? ` (${utils.recurrenceCustomName(stage, rule.stageRecurrence) || `recurrence ${rule.stageRecurrence + 1}`})`
      : ''
    return `On completion of "${stageName}"${recLabel}`
  }
  return '—'
}

export default function Anchors () {
  const { trial, anchors, addAnchor, deleteAnchor, setAnchors, user, stages } = useStore()
  const [editingAnchor, setEditingAnchor] = useState(null) // null = not editing, {} = new, existing = editing
  const [loading, setLoading] = useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const canManage = utils.canManageTrial(user, trial)

  useEffect(() => {
    if (!trial?._id) return
    setLoading(true)
    api.anchors.list(trial._id, d => {
      setAnchors(d.anchors || [])
      setLoading(false)
    }, () => setLoading(false))
  }, [trial?._id])

  function handleSaveNew (data, done) {
    api.anchors.create(trial._id, data, anchor => {
      addAnchor(anchor)
      setEditingAnchor(null)
      utils.toast('success', 'Anchor created')
      done()
    }, err => {
      utils.toast('error', 'Unable to create anchor', err.message)
      done()
    })
  }

  function handleSaveEdit (data, done) {
    api.anchors.update(editingAnchor._id, data, anchor => {
      addAnchor(anchor)
      setEditingAnchor(null)
      utils.toast('success', 'Anchor updated')
      done()
    }, err => {
      utils.toast('error', 'Unable to update anchor', err.message)
      done()
    })
  }

  function handleDelete (anchorId) {
    api.anchors.delete(anchorId, () => {
      deleteAnchor(anchorId)
      setConfirmDeleteId(null)
      utils.toast('success', 'Anchor deleted')
    }, err => utils.toast('error', 'Unable to delete anchor', err.message))
  }

  if (loading) return null

  return (
    <Stack gap={5}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
        <Box>
          <Heading as='h2' size='lg'>Anchors</Heading>
          <Text>Anchor dates are per-participant reference dates used to schedule stages and other items relative to specific events.</Text>
        </Box>
        {canManage &&
          <Button colorPalette='primary' onClick={() => setEditingAnchor({})}>Add anchor</Button>}
      </Stack>

      {anchors?.length === 0 && editingAnchor == null && (
        <EmptyBox icon='box' title='No anchors defined' description='Add anchor dates to schedule stages relative to participant-specific events.' />
      )}

      {anchors?.length > 0 && (
        <SimpleTable
          asCard
          headers={['Name', 'Description', 'Assignment rule', ...(canManage ? [''] : [])]}
          rows={anchors.map(a => [
            <Stack key='name' gap={0}>
              <Text fontWeight='semibold'>{a.name}</Text>
            </Stack>,
            <Text key='desc' fontSize='sm' color='fg.muted'>{a.description || <Badge>No description</Badge>}</Text>,
            <Text key='rule' fontSize='sm'>{ruleDescription(a.assignmentRule, stages)}</Text>,
            ...(canManage
              ? [
                <Stack key='actions' direction='row' justifyContent='end' gap={1}>
                  <Button size='sm' colorPalette='primary' onClick={() => setEditingAnchor(a)}>Edit</Button>
                  <Button size='sm' variant='ghost' onClick={() => setConfirmDeleteId(a._id)}><Icon as={FaTrash} /></Button>
                </Stack>
                ]
              : [])
          ])}
        />
      )}

      {/* Create / edit modal */}
      <SimpleModal
        isOpen={editingAnchor !== null}
        onClose={() => setEditingAnchor(null)}
        header={editingAnchor?._id ? `Edit anchor: ${editingAnchor?.name || ''}` : 'New anchor'}
        body={
          editingAnchor !== null
            ? (
              <AnchorForm
                anchor={editingAnchor?._id ? editingAnchor : undefined}
                stages={stages}
                onSave={editingAnchor?._id ? handleSaveEdit : handleSaveNew}
                onCancel={() => setEditingAnchor(null)}
              />
              )
            : null
        }
      />

      {/* Delete confirm modal */}
      <SimpleModal
        isOpen={confirmDeleteId != null}
        onClose={() => setConfirmDeleteId(null)}
        header='Delete this anchor?'
        body={
          <Stack gap={3}>
            <Alert status='warning'>Deleting this anchor will remove all scheduled stages that depend on it, and clear this anchor date from all participants.</Alert>
            <Text>This action cannot be undone.</Text>
          </Stack>
        }
        footer={
          <Stack direction='row' justifyContent='end' gap={2}>
            <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
            <Button colorPalette='red' onClick={() => handleDelete(confirmDeleteId)}>Delete anchor</Button>
          </Stack>
        }
      />
    </Stack>
  )
}
